import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CalendarDays,
  CheckCircle,
  History,
  Loader2,
  Lock,
  RotateCcw,
  Save,
  ShieldCheck,
  Unlock,
  X
} from 'lucide-react';
import { Toast } from './ui/Toast';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';
import { api } from '../services/api';
import { usePermissionsEtatSalaires } from '../hooks/usePermissionsEtatSalaires';

const DEFAULTS = {
  max_modifications_salaire_fixe: 2,
  max_avances_salaire_mois: 1,
  pourcentage_max_avance_salaire: 50,
  avance_jour_debut: 1,
  avance_jour_fin: 20,
  etat_salaires_jour_debut: 21,
  etat_salaires_jour_fin: 24,
  demande_dotation_jour_debut: 24,
  demande_dotation_jour_fin: 26,
  validation_centrale_jour_debut: 26,
  validation_centrale_jour_fin: 28,
  paiement_salaires_jour_debut: 28,
  paiement_salaires_jour_fin: 31,
  verrouillage_strict_paiement_salaires: false
};

const clampLimit = (value) => {
  const parsed = Math.round(Number(value || 0));
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  if (parsed > 12) return 12;
  return parsed;
};

const clampPercentage = (value) => {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  if (parsed > 100) return 100;
  return Math.round(parsed * 100) / 100;
};

const clampDay = (value) => {
  const parsed = Math.round(Number(value || 0));
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  if (parsed > 31) return 31;
  return parsed;
};

const normalizeBoolean = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined || value === '') return false;
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'oui', 'yes', 'on', 'active', 'actif'].includes(normalized)) return true;
  if (['0', 'false', 'non', 'no', 'off', 'inactive', 'inactif'].includes(normalized)) return false;
  return Boolean(Number(value));
};

const normalizeParametres = (data = {}) => ({
  max_modifications_salaire_fixe: clampLimit(data.max_modifications_salaire_fixe ?? DEFAULTS.max_modifications_salaire_fixe),
  max_avances_salaire_mois: clampLimit(data.max_avances_salaire_mois ?? DEFAULTS.max_avances_salaire_mois),
  pourcentage_max_avance_salaire: clampPercentage(data.pourcentage_max_avance_salaire ?? DEFAULTS.pourcentage_max_avance_salaire),
  avance_jour_debut: clampDay(data.avance_jour_debut ?? DEFAULTS.avance_jour_debut),
  avance_jour_fin: clampDay(data.avance_jour_fin ?? DEFAULTS.avance_jour_fin),
  etat_salaires_jour_debut: clampDay(data.etat_salaires_jour_debut ?? DEFAULTS.etat_salaires_jour_debut),
  etat_salaires_jour_fin: clampDay(data.etat_salaires_jour_fin ?? DEFAULTS.etat_salaires_jour_fin),
  demande_dotation_jour_debut: clampDay(data.demande_dotation_jour_debut ?? DEFAULTS.demande_dotation_jour_debut),
  demande_dotation_jour_fin: clampDay(data.demande_dotation_jour_fin ?? DEFAULTS.demande_dotation_jour_fin),
  validation_centrale_jour_debut: clampDay(data.validation_centrale_jour_debut ?? DEFAULTS.validation_centrale_jour_debut),
  validation_centrale_jour_fin: clampDay(data.validation_centrale_jour_fin ?? DEFAULTS.validation_centrale_jour_fin),
  paiement_salaires_jour_debut: clampDay(data.paiement_salaires_jour_debut ?? DEFAULTS.paiement_salaires_jour_debut),
  paiement_salaires_jour_fin: clampDay(data.paiement_salaires_jour_fin ?? DEFAULTS.paiement_salaires_jour_fin),
  verrouillage_strict_paiement_salaires: normalizeBoolean(data.verrouillage_strict_paiement_salaires ?? DEFAULTS.verrouillage_strict_paiement_salaires)
});

const sameParametres = (a = {}, b = {}) => {
  const first = normalizeParametres(a);
  const second = normalizeParametres(b);
  return Object.keys(DEFAULTS).every((key) => String(first[key]) === String(second[key]));
};

// `bloquant` : hors de ces dates, pendant le mois de paie en cours, le serveur
// refuse l'opération (utils/fenetresPaie.js côté serveur). Les autres fenêtres
// sont indicatives.
const payrollWindows = [
  {
    title: 'Avances sur salaire',
    description: "Hors de ces dates, aucune nouvelle avance ne peut être créée.",
    startKey: 'avance_jour_debut',
    endKey: 'avance_jour_fin',
    bloquant: true
  },
  {
    title: "Préparation de l'état des salaires",
    description: 'Période conseillée pour contrôler pointages, retenues et avances avant la dotation.',
    startKey: 'etat_salaires_jour_debut',
    endKey: 'etat_salaires_jour_fin',
    bloquant: false
  },
  {
    title: 'Demande de dotation',
    description: 'Hors de ces dates, la demande de dotation salaires à la caisse centrale est refusée.',
    startKey: 'demande_dotation_jour_debut',
    endKey: 'demande_dotation_jour_fin',
    bloquant: true
  },
  {
    title: 'Validation caisse centrale',
    description: 'Période conseillée pour vérifier la demande et mettre les fonds à disposition.',
    startKey: 'validation_centrale_jour_debut',
    endKey: 'validation_centrale_jour_fin',
    bloquant: false
  },
  {
    title: 'Paiement des salaires',
    description: 'Hors de ces dates, le paiement des salaires est refusé.',
    startKey: 'paiement_salaires_jour_debut',
    endKey: 'paiement_salaires_jour_fin',
    bloquant: true
  }
];

const formatDateHeure = (valeur) => {
  if (!valeur) return 'date inconnue';
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return 'date inconnue';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
};

const ParametresSalaires = ({ onBackToDashboard }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parametres, setParametres] = useState(DEFAULTS);
  const [initialParametres, setInitialParametres] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Mois figes : un etat des salaires valide interdit toute nouvelle avance sur
  // le mois. Le defigeage supprime cette validation pour rouvrir la saisie.
  const [moisFiges, setMoisFiges] = useState([]);
  const [loadingMoisFiges, setLoadingMoisFiges] = useState(false);
  const [moisADefiger, setMoisADefiger] = useState(null);
  // Défiger est réservé aux profils Directeur Général et Promoteur
  const { peutDefiger } = usePermissionsEtatSalaires();
  const [motifDefigeage, setMotifDefigeage] = useState('');
  const [defigeageEnCours, setDefigeageEnCours] = useState(false);

  const anneeScolaireId = localStorage.getItem('annee_scolaire_id') || '1';
  const anneeScolaireNom = localStorage.getItem('annee_scolaire_nom') || localStorage.getItem('annee_scolaire') || 'Année courante';

  const hasChanges = useMemo(() => {
    if (!initialParametres) return false;
    return !sameParametres(parametres, initialParametres);
  }, [initialParametres, parametres]);

  const showToast = (type, message) => {
    setToast({ show: true, type, message });
  };

  const closeToast = () => {
    setToast((current) => ({ ...current, show: false }));
  };

  const setField = (key, value, normalizer = clampDay) => {
    setParametres((current) => ({
      ...current,
      [key]: value === '' ? '' : normalizer(value)
    }));
  };

  const setBooleanField = (key, value) => {
    setParametres((current) => ({
      ...current,
      [key]: Boolean(value)
    }));
  };

  const loadParametres = async () => {
    setLoading(true);
    try {
      const response = await api.parametresSalaires.getByAnnee(anneeScolaireId);
      if (!response.success) {
        throw new Error(response.message || response.error || 'Parametres indisponibles');
      }

      const nextParametres = normalizeParametres(response.data || DEFAULTS);
      setParametres(nextParametres);
      setInitialParametres(nextParametres);
    } catch (error) {
      showToast('error', error.message || 'Erreur lors du chargement des parametres salaires');
    } finally {
      setLoading(false);
    }
  };

  const loadMoisFiges = async () => {
    setLoadingMoisFiges(true);
    try {
      const response = await api.etatSalaires.getMoisFiges(anneeScolaireId);
      setMoisFiges(response.success ? (response.data || []) : []);
    } catch (error) {
      console.error('Erreur lecture mois figes:', error);
      setMoisFiges([]);
    } finally {
      setLoadingMoisFiges(false);
    }
  };

  useEffect(() => {
    loadParametres();
    loadMoisFiges();
  }, [anneeScolaireId]);

  const handleDefigerMois = async () => {
    if (!moisADefiger) return;

    if (motifDefigeage.trim().length < 10) {
      showToast('error', 'Le motif du défigeage est obligatoire (10 caractères minimum).');
      return;
    }

    setDefigeageEnCours(true);
    try {
      const response = await api.etatSalaires.defigerMois({
        annee_scolaire_id: anneeScolaireId,
        mois: moisADefiger.mois,
        annee: moisADefiger.annee,
        motif: motifDefigeage.trim()
      });

      if (!response.success) {
        throw new Error(response.message || response.error || 'Défigeage refusé');
      }

      showToast('success', response.message || 'Mois défigé');
      setMoisADefiger(null);
      setMotifDefigeage('');
      await loadMoisFiges();
    } catch (error) {
      showToast('error', error.message || 'Erreur lors du défigeage du mois');
    } finally {
      setDefigeageEnCours(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = normalizeParametres(parametres);
      const response = await api.parametresSalaires.updateByAnnee(anneeScolaireId, payload);
      if (!response.success) {
        throw new Error(response.message || response.error || 'Sauvegarde refusee');
      }

      const saved = normalizeParametres(response.data || payload);
      setParametres(saved);
      setInitialParametres(saved);
      showToast('success', 'Parametres salaires enregistres');
    } catch (error) {
      showToast('error', error.message || 'Erreur lors de la sauvegarde des parametres salaires');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!initialParametres) return;
    setParametres(initialParametres);
  };

  return (
    <div className="w-full space-y-6">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}

      <EnTeteParametrage
        onRetour={onBackToDashboard}
        icone={ShieldCheck}
        titre="Paramétrage du contrôle des salaires"
        sousTitre="Plafonds, avances et calendrier mensuel de paie"
      />

      {loading ? (
        <ChargementSection accent="#246990" libelle="Chargement des paramètres salaires…" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-lg bg-[#2b7da8] p-5 text-white shadow-sm">
              <p className="text-sm font-semibold opacity-90">Modifications salaire</p>
              <p className="mt-3 text-3xl font-bold">{clampLimit(parametres.max_modifications_salaire_fixe)}</p>
              <p className="mt-1 text-sm opacity-90">modification(s) approuvée(s) par employé</p>
            </div>
            <div className="rounded-lg bg-[#c66f5d] p-5 text-white shadow-sm">
              <p className="text-sm font-semibold opacity-90">Avances par mois</p>
              <p className="mt-3 text-3xl font-bold">{clampLimit(parametres.max_avances_salaire_mois)}</p>
              <p className="mt-1 text-sm opacity-90">avance(s) maximum par employe</p>
            </div>
            <div className="rounded-lg bg-[#319190] p-5 text-white shadow-sm">
              <p className="text-sm font-semibold opacity-90">Plafond d'avance</p>
              <p className="mt-3 text-3xl font-bold">{clampPercentage(parametres.pourcentage_max_avance_salaire)}%</p>
              <p className="mt-1 text-sm opacity-90">du salaire mensuel de reference</p>
            </div>
            <div className="rounded-lg bg-[#263244] p-5 text-white shadow-sm">
              <p className="text-sm font-semibold opacity-90">Etat des salaires</p>
              <p className="mt-3 text-2xl font-bold">Jour {parametres.etat_salaires_jour_debut} - {parametres.etat_salaires_jour_fin}</p>
              <p className="mt-1 text-sm opacity-90">fenetre de preparation mensuelle</p>
            </div>
          </div>

          <CarteReglages
            titre="Règles sensibles de paie"
            accent="#246990"
            compteur={anneeScolaireNom ? `Année ${anneeScolaireNom}` : null}
          >
            <p className="mb-5 text-xs text-gray-500">
              Ces limites protègent les modifications de salaire et les avances avant
              préparation de l'état mensuel.
            </p>

            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,420px)_1fr]">
                <label className="block">
                  <span className="text-sm font-semibold text-slate-800">Nombre maximum de modifications validées</span>
                  <div className="mt-2 flex rounded-lg border border-slate-300 bg-white focus-within:border-[#246990] focus-within:ring-2 focus-within:ring-[#246990]/20">
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={parametres.max_modifications_salaire_fixe}
                      onChange={(event) => setField('max_modifications_salaire_fixe', event.target.value, clampLimit)}
                      className="w-full rounded-l-lg border-0 px-4 py-3 text-lg font-bold text-slate-900 outline-none"
                    />
                    <span className="rounded-r-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500">
                      par employe
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Valeur autorisee : de 1 a 12 modifications approuvees.</p>
                </label>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                    <CheckCircle size={18} className="mb-2 text-emerald-700" />
                    <p className="text-sm font-semibold text-emerald-900">Demandes approuvees</p>
                    <p className="mt-1 text-xs text-emerald-800">Comptent dans le plafond annuel.</p>
                  </div>
                  <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
                    <AlertCircle size={18} className="mb-2 text-amber-700" />
                    <p className="text-sm font-semibold text-amber-900">Demandes en attente</p>
                    <p className="mt-1 text-xs text-amber-800">Bloque une nouvelle demande pour le meme employe.</p>
                  </div>
                  <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                    <History size={18} className="mb-2 text-blue-700" />
                    <p className="text-sm font-semibold text-blue-900">Historique conserve</p>
                    <p className="mt-1 text-xs text-blue-800">Demandes, validations et mois d'effet restent traces.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#246990]/20 bg-[#f4f9fc] p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Avances sur salaire</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Ces regles limitent les avances creees pour un meme employe sur un meme mois scolaire.
                    </p>
                  </div>
                  <span className="rounded-full border border-[#246990]/20 bg-white px-3 py-1 text-xs font-semibold uppercase text-[#246990]">
                    Garde-fou paie
                  </span>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-semibold text-slate-800">Nombre maximum d'avances par mois</span>
                    <div className="mt-2 flex rounded-lg border border-slate-300 bg-white focus-within:border-[#246990] focus-within:ring-2 focus-within:ring-[#246990]/20">
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={parametres.max_avances_salaire_mois}
                        onChange={(event) => setField('max_avances_salaire_mois', event.target.value, clampLimit)}
                        className="w-full rounded-l-lg border-0 px-4 py-3 text-lg font-bold text-slate-900 outline-none"
                      />
                      <span className="rounded-r-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500">
                        par mois
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Exemple : 1 signifie une seule avance active sur le mois.</p>
                  </label>

                  <label className="block">
                    <span className="text-sm font-semibold text-slate-800">Pourcentage maximum du salaire</span>
                    <div className="mt-2 flex rounded-lg border border-slate-300 bg-white focus-within:border-[#246990] focus-within:ring-2 focus-within:ring-[#246990]/20">
                      <input
                        type="number"
                        min="1"
                        max="100"
                        step="0.5"
                        value={parametres.pourcentage_max_avance_salaire}
                        onChange={(event) => setField('pourcentage_max_avance_salaire', event.target.value, clampPercentage)}
                        className="w-full rounded-l-lg border-0 px-4 py-3 text-lg font-bold text-slate-900 outline-none"
                      />
                      <span className="rounded-r-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-500">
                        %
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-slate-500">Exemple : 50 limite les avances du mois a la moitie du salaire.</p>
                  </label>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${parametres.verrouillage_strict_paiement_salaires ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {parametres.verrouillage_strict_paiement_salaires ? <Lock size={20} /> : <Unlock size={20} />}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-slate-900">Verrouillage des modules de paiement</h3>
                      <p className="mt-1 text-sm text-slate-600">
                        Ce réglage contrôle uniquement l'accès visuel aux modules Enseignants, Personnel administratif et Employés hybrides.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={parametres.verrouillage_strict_paiement_salaires}
                    onClick={() => setBooleanField('verrouillage_strict_paiement_salaires', !parametres.verrouillage_strict_paiement_salaires)}
                    className={`inline-flex min-w-[132px] items-center justify-between gap-3 rounded-full px-2 py-1 text-sm font-semibold transition ${parametres.verrouillage_strict_paiement_salaires ? 'bg-amber-600 text-white' : 'bg-emerald-600 text-white'}`}
                  >
                    <span className="px-2">{parametres.verrouillage_strict_paiement_salaires ? 'Activé' : 'Désactivé'}</span>
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm">
                      {parametres.verrouillage_strict_paiement_salaires ? <Lock size={15} /> : <Unlock size={15} />}
                    </span>
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                  <div className="rounded-lg border border-amber-200 bg-white p-3 text-amber-900">
                    <p className="font-semibold">Activé</p>
                    <p className="mt-1 text-xs leading-5">Les cartes de paiement sont floutées tant que l'état des salaires n'est pas validé.</p>
                  </div>
                  <div className="rounded-lg border border-emerald-200 bg-white p-3 text-emerald-900">
                    <p className="font-semibold">Désactivé</p>
                    <p className="mt-1 text-xs leading-5">Les modules restent consultables pour identifier les pointages manquants, mais le paiement reste bloqué par le contrôle serveur.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#246990]/20 bg-white p-5">
                <div className="mb-5 flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#246990]/10 text-[#246990]">
                    <CalendarDays size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Calendrier mensuel de paie</h3>
                    <p className="text-sm text-slate-600">
                      Numéros de jour du mois de paie (jour 1 = début du mois configuré).
                    </p>
                  </div>
                </div>

                <div className="mb-4 space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  <p>
                    <strong>Pendant le mois de paie en cours, le système bloque les opérations hors des dates</strong> des
                    fenêtres marquées « Bloquant » : création d'avance, demande de dotation, paiement des salaires.
                    Les deux autres fenêtres sont indicatives.
                  </p>
                  <p>
                    Chaque mois, une date qui tombe un samedi ou un dimanche est reportée au jour ouvré suivant
                    (au précédent si le suivant sort du mois), et une fin au-delà du dernier jour s'arrête à ce jour.
                    Les dates réelles de chaque mois s'affichent dans l'état des salaires.
                  </p>
                </div>

                <div className="space-y-3">
                  {payrollWindows.map((item) => (
                    <div key={item.startKey} className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[1fr_104px_160px_160px] lg:items-center">
                      <div>
                        <p className="text-sm font-bold text-slate-900">{item.title}</p>
                        <p className="text-xs text-slate-500">{item.description}</p>
                      </div>
                      {/* Colonne dédiée : le badge ne dépend plus de la longueur du
                          titre et s'aligne d'une ligne à l'autre (DESIGN-SYSTEM.md). */}
                      <span className={`inline-flex h-6 w-[92px] items-center justify-center rounded-full border text-xs font-medium ${item.bloquant
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : 'border-gray-200 bg-white text-gray-600'}`}
                      >
                        {item.bloquant ? 'Bloquant' : 'Indicatif'}
                      </span>
                      <label className="block">
                        <span className="text-xs font-semibold uppercase text-slate-500">Debut</span>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={parametres[item.startKey]}
                          onChange={(event) => setField(item.startKey, event.target.value, clampDay)}
                          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                        />
                      </label>
                      <label className="block">
                        <span className="text-xs font-semibold uppercase text-slate-500">Fin</span>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={parametres[item.endKey]}
                          onChange={(event) => setField(item.endKey, event.target.value, clampDay)}
                          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-bold text-slate-900 outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* ============ MOIS FIGES ============ */}
              <div className="rounded-lg border border-[#246990]/20 bg-white p-5">
                <div className="mb-5 flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                    <Lock size={20} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">Mois figés</h3>
                    <p className="text-sm text-slate-600">
                      Un état des salaires validé fige le mois : plus aucune avance ne peut y être
                      imputée, car la dotation a déjà été calibrée sur le net à payer arrêté.
                      Défiger rouvre la saisie et annule la validation comptable.
                    </p>
                  </div>
                </div>

                {loadingMoisFiges ? (
                  <div className="flex items-center gap-2 px-1 py-3 text-sm text-slate-500">
                    <Loader2 className="animate-spin" size={16} />
                    Chargement des mois figés...
                  </div>
                ) : moisFiges.length === 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                    Aucun mois figé pour {anneeScolaireNom}. Les avances restent possibles sur toute
                    l'année, dans la fenêtre du calendrier ci-dessus.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {moisFiges.map((mois) => (
                      <div
                        key={`${mois.annee}-${mois.mois}`}
                        className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 lg:flex-row lg:items-center lg:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-slate-900">
                            {mois.mois_label || `${mois.mois}/${mois.annee}`}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-600">
                            Validé le {formatDateHeure(mois.validated_at)}
                            {mois.validated_by_nom ? ` par ${mois.validated_by_nom}` : ''}
                            {' · '}
                            {Number(mois.montant_a_doter || 0).toLocaleString('fr-FR')} FCFA dotés
                            {' · '}
                            {mois.effectif_unique} employé{mois.effectif_unique > 1 ? 's' : ''}
                          </p>
                        </div>
                        {peutDefiger && (
                        <button
                          type="button"
                          onClick={() => { setMoisADefiger(mois); setMotifDefigeage(''); }}
                          className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
                        >
                          <Unlock size={16} />
                          Défiger ce mois
                        </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                Les mois deja payes, clotures ou avec avance sur salaire restent verrouilles. Le nouveau salaire ne s applique qu a partir du mois d effet valide par le Centre de Support. Les avances sont refusees automatiquement si elles depassent les limites configurees ici.
              </div>

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-5">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={!hasChanges || saving}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-500 transition-all hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <RotateCcw size={13} />
                  Réinitialiser
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={!hasChanges || saving}
                  className={`flex items-center gap-1.5 rounded-lg border border-transparent px-4 py-2 text-xs font-semibold text-white shadow-sm transition-all ${
                    hasChanges ? 'bg-[#246990] hover:shadow-md' : 'cursor-not-allowed bg-gray-300 shadow-none'
                  }`}
                >
                  {saving
                    ? <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    : <Save size={13} />}
                  Enregistrer
                </button>
              </div>
            </div>
          </CarteReglages>
        </>
      )}

      {/* ============ CONFIRMATION DE DEFIGEAGE ============ */}
      {moisADefiger && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex flex-shrink-0 items-start justify-between gap-4 bg-amber-600 px-6 py-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/80">
                  Etat des salaires
                </p>
                <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
                  <Unlock size={20} />
                  Défiger {moisADefiger.mois_label || `${moisADefiger.mois}/${moisADefiger.annee}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setMoisADefiger(null)}
                disabled={defigeageEnCours}
                className="rounded-lg p-2 text-white transition-colors hover:bg-white/20 disabled:opacity-50"
                aria-label="Fermer"
              >
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <p className="font-semibold">Ce que fait le défigeage</p>
                <p className="mt-1 leading-relaxed">
                  La validation comptable de ce mois est annulée. Les avances redeviennent
                  possibles, et l'état des salaires devra être revalidé avant tout paiement.
                  L'opération est tracée. Elle est refusée si des salaires ont déjà été payés
                  sur le mois.
                </p>
              </div>

              <div>
                <label
                  htmlFor="motif-defigeage"
                  className="mb-1.5 block text-sm font-medium text-slate-700"
                >
                  Motif du défigeage <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="motif-defigeage"
                  rows={3}
                  value={motifDefigeage}
                  onChange={(event) => setMotifDefigeage(event.target.value)}
                  placeholder="Ex : avance oubliée pour un employé, correction d'un pointage, erreur de saisie..."
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                />
                <p className="mt-1 text-xs text-slate-500">
                  10 caractères minimum. Ce motif est conservé dans le journal des défigeages.
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 flex-col gap-3 border-t border-slate-200 bg-white px-6 py-4 sm:flex-row">
              <button
                type="button"
                onClick={() => setMoisADefiger(null)}
                disabled={defigeageEnCours}
                className="flex-1 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDefigerMois}
                disabled={defigeageEnCours || motifDefigeage.trim().length < 10}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {defigeageEnCours ? <Loader2 className="animate-spin" size={16} /> : <Unlock size={16} />}
                {defigeageEnCours ? 'Défigeage...' : 'Confirmer le défigeage'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParametresSalaires;
